import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { RefreshCw, Home } from 'lucide-react';
import { track } from '@/lib/analytics';
import { isStaleBuildError, purgeAppCaches, recoverFromChunkFailure } from '@/lib/chunkRecovery';

/**
 * Root errorElement — renders standalone (outside <Layout/>) so it still works
 * when the layout itself is what crashed. Covers both router 404s and thrown
 * errors (e.g. a lazy chunk that failed to load).
 *
 * It used to discard the error entirely, which made every field report of this
 * screen undiagnosable: the message is the only thing that says whether the
 * page failed to download or a component threw. It is now logged, reported to
 * Clarity, and shown on the page so a user can read it back.
 */
export function ErrorPage() {
  const error = useRouteError();
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  const detail = is404
    ? null
    : error instanceof Error
      ? error.message
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : error
          ? String(error)
          : null;

  useEffect(() => {
    if (is404) return;
    console.error('Route error:', error);
    track('route_error', { route_error_path: window.location.pathname, route_error: detail ?? 'unknown' });

    // A page left open across a deploy asks for files the deploy pruned. That
    // is not a crash the visitor should have to read about — purge the stale
    // caches and reload once. `recoverFromChunkFailure` records a timestamped
    // attempt, so a genuine repeat failure shows this page instead of looping.
    if (!isStaleBuildError(error)) return;
    void recoverFromChunkFailure({
      storage: sessionStorage,
      purge: purgeAppCaches,
      reload: () => window.location.reload(),
      now: () => Date.now(),
    });
  }, [error, is404, detail]);

  // The reload only helps if it can reach the network. When a stale service
  // worker precache is what is serving the dead page, purge it first.
  const hardReload = async () => {
    try {
      await purgeAppCaches();
    } catch {
      // Reload regardless — it fixes the common, no-service-worker case.
    }
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-950 px-6 text-center">
      <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-4 py-1.5 text-xs font-medium text-gold-300">
        ✦ Kundali AI
      </p>
      <h1 className="font-display text-5xl font-bold text-white md:text-6xl">
        {is404 ? '404' : 'Oops!'}
      </h1>
      <p className="mt-4 max-w-md text-lg text-gray-300">
        {is404
          ? "This page doesn't exist — the stars couldn't find it."
          : 'Something went wrong loading this page. It usually fixes itself with a quick reload.'}
      </p>
      {detail && (
        <p className="mt-3 max-w-md break-words font-mono text-xs text-gray-500">{detail}</p>
      )}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {!is404 && (
          <button
            onClick={hardReload}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 font-medium text-white transition-colors hover:bg-primary-500"
          >
            <RefreshCw size={16} /> Reload page
          </button>
        )}
        <a
          href="/"
          className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-5 py-2.5 font-medium text-gray-200 transition-colors hover:bg-white/5"
        >
          <Home size={16} /> Back to home
        </a>
      </div>
    </div>
  );
}
